/**
 * The first online evening (WP-N2, docs/ONLINE-UI.md "The first evening"): a ladder of eight rungs the NEXT
 * clipping walks a new character up, one plain line and one map mark at a time —
 *
 *   your loaner → a job off a board → the bank → Sid's back room → a crew → Night Freight → a gang → premises.
 *
 * Each rung is done by something the city's books (or this device) know: you've driven, a job was paid, there
 * is money in the bank, you've a weapon or have stood at Sid's door, you're in a crew or a gang, a freight run
 * paid you, you're in a gang, you hold premises. The first rung not done is the one shown. The same rung index
 * decides what the book shows (disclosure.ts): the advanced pages open as the ladder reaches them.
 * Pure; unit-tested in onboarding.test.ts.
 */
import { GANG_CREATE_COST } from '../../shared/gangs';
import type { NextStep, Waypoint } from './nextStep';

export interface LadderFacts {
  /** Been at the wheel of a car as this character (or owns one). */
  drove: boolean;
  /** Halloran's loaner, where it stands when it is out on the street. */
  loaner?: Waypoint;
  /** The loaner's model ("Austral Minnow"): the prompt names the car, so the clipping does too. */
  loanerModel?: string;
  /** Halloran Motors (a loaner in the garage, or one to ask for). */
  garage?: Waypoint;
  /** Courier / recovery jobs paid, all time (the record). */
  jobs: number;
  /** Money in the bank. */
  banked: boolean;
  bank?: Waypoint;
  /** Owns a weapon, or has stood at Sid's door. */
  sid: boolean;
  sidAt?: Waypoint;
  /** In a crew (or a gang), or has been on a crew run. */
  crewed: boolean;
  /** A pub to meet the crew at. */
  pub?: Waypoint;
  /** Night Freight runs that paid you (the record). */
  freightRuns: number;
  crewOnline: number;
  pier?: Waypoint;
  inGang: boolean;
  /** Holds premises (or the firm runs a district). */
  premises: boolean;
  /** The nearest premises to let. */
  site?: Waypoint;
  /**
   * Nobody else in the city (the shard roster is just you): the crew and Night Freight rungs need another
   * player, so the NEXT clipping steps over them while you're alone (they come back when someone joins).
   */
  alone?: boolean;
}

export const RUNGS = ['car', 'job', 'bank', 'sid', 'crew', 'freight', 'gang', 'premises'] as const;
export type Rung = (typeof RUNGS)[number];

const DONE: Record<Rung, (f: LadderFacts) => boolean> = {
  car: (f) => f.drove,
  job: (f) => f.jobs >= 1,
  bank: (f) => f.banked,
  sid: (f) => f.sid,
  crew: (f) => f.crewed || f.inGang || f.freightRuns > 0,
  freight: (f) => f.freightRuns > 0,
  gang: (f) => f.inGang,
  premises: (f) => f.premises,
};

/** Index of the first rung not done (RUNGS.length = the whole ladder climbed). */
export function ladderRung(f: LadderFacts): number {
  const i = RUNGS.findIndex((r) => !DONE[r](f));
  return i < 0 ? RUNGS.length : i;
}

/** Rungs that need another player in the city. */
const TOGETHER: readonly Rung[] = ['crew', 'freight'];

/**
 * The rung the NEXT clipping shows: the first not done, stepping over the crew rungs while you're alone. (What
 * the book shows still follows `ladderRung`: being alone opens nothing.)
 */
export function shownRung(f: LadderFacts): number {
  const i = RUNGS.findIndex((r) => !DONE[r](f) && !(f.alone && TOGETHER.includes(r)));
  return i < 0 ? RUNGS.length : i;
}

/** Has the ladder reached (or passed) `rung`? */
export function reached(f: LadderFacts, rung: Rung): boolean {
  return ladderRung(f) >= RUNGS.indexOf(rung);
}

const mark = (w: Waypoint | undefined): { target?: Waypoint } => (w ? { target: w } : {});

/** The NEXT clipping for the first rung not done, or null once the ladder is climbed. */
export function ladderStep(f: LadderFacts, board: Waypoint | undefined, touch: boolean): NextStep | null {
  const rung = RUNGS[shownRung(f)];
  const base = { kind: 'online' as const };
  switch (rung) {
    case 'car':
      // The first thing a new character sees is a row of parked cars, each with a USE prompt: the one that is
      // theirs says "Get in", a stranger's says "Steal" — and a theft brings the police. Say which is which.
      return f.loaner
        ? {
            ...base,
            title: 'Your first motor',
            text: f.loanerModel
              ? `Halloran’s lent you a car, the ${f.loanerModel}. Follow the arrow to it and get in.`
              : 'Halloran’s lent you a car. Follow the arrow to it and get in.',
            hint: f.loanerModel
              ? `Press E where it says “Get in the ${f.loanerModel}”. A car that says “Steal” isn’t yours — take it and the police come looking.`
              : 'Press E where it says “Get in”. A car that says “Steal” isn’t yours — take it and the police come looking.',
            target: { ...f.loaner, label: 'Halloran’s loaner' },
          }
        : {
            ...base,
            title: 'Your first motor',
            text: 'Fetch Halloran’s loaner from Halloran Motors — press E at the door.',
            hint: 'Lost it? Halloran lends you another there after a while.',
            ...mark(f.garage),
          };
    case 'job':
      return {
        ...base,
        title: 'Your first job',
        text: board ? `Take a job off the board at ${board.label} — press E at the door.` : 'Take a job off a board — phone boxes, pubs, Halloran’s. Press E at the door.',
        hint: 'Courier work: collect a parcel, drive it to the drop. The clipping shows where.',
        ...mark(board),
      };
    case 'bank':
      return {
        ...base,
        title: 'Bank your pay',
        text: 'Pay your money in at the bank or a cashpoint — press E at the door.',
        hint: 'A fine or a mugging takes what’s in your pocket. The bank keeps the rest.',
        ...mark(f.bank),
      };
    case 'sid':
      return {
        ...base,
        title: 'Sid’s back room',
        text: 'Sid Marlowe sells tools of the trade at Gorringe’s scrap yard. Go and see him — press E at his door.',
        hint: 'A cosh or a crowbar for the street. A gun costs more, and the police take it seriously.',
        ...mark(f.sidAt),
      };
    case 'crew':
      return {
        ...base,
        title: 'Find a crew',
        text: touch ? 'Crew up with a mate: book → Crew, and invite them by name.' : 'Crew up with a mate: open the crew sheet (C) and invite them by name.',
        // A crew needs another player: a solo evening must not read as stuck here, so say what pays without one.
        hint: 'Only a crew can run Night Freight. On your own tonight? The job boards and Lenny’s list (in your book) pay without one — ask in city chat for a mate.',
        ...mark(f.pub),
      };
    case 'freight':
      return {
        ...base,
        title: 'Your first Night Freight',
        text: f.crewOnline >= 2 ? 'Meet your crew at Bonded Warehouse No. 3 and press E at the pier.' : 'Night Freight needs two of you: get your crew online, then meet at Bonded Warehouse No. 3.',
        hint:
          f.crewOnline >= 2
            ? 'One of you drives a car with a boot. Crates are bought at the pier and sold at the drop.'
            : 'Crew not about? The job boards and Lenny’s list (in your book) pay in the meantime.',
        ...mark(f.pier),
      };
    case 'gang':
      return {
        ...base,
        title: 'A firm of your own',
        text: touch ? `Found a gang (£${GANG_CREATE_COST.toLocaleString('en-GB')}) in your book → Gang, or get asked into one.` : `Found a gang (£${GANG_CREATE_COST.toLocaleString('en-GB')}) in the gang ledger (G), or get asked into one.`,
        hint: 'A gang shares a treasury, premises and turf. You can leave whenever you like.',
      };
    case 'premises':
      return {
        ...base,
        title: 'Premises',
        text: 'Rent a lockup to make goods in — press E at a lockup door.',
        hint: 'Rent is paid daily from the bank. What you make sells to the buyers in your book.',
        ...mark(f.site),
      };
    default:
      return null;
  }
}
